export const CURRENCIES = [
  { code: "NGN", symbol: "₦", label: "Nigerian Naira" },
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GHS", symbol: "₵", label: "Ghanaian Cedi" },
  { code: "KES", symbol: "KSh", label: "Kenyan Shilling" },
  { code: "ZAR", symbol: "R", label: "South African Rand" },
  { code: "CAD", symbol: "C$", label: "Canadian Dollar" },
];

export function symbolFor(code) {
  const c = CURRENCIES.find((x) => x.code === (code || "NGN"));
  return c ? c.symbol : "₦";
}

export function fmtMoneyList(items, emptyCurrency = "NGN") {
  if (!items || items.length === 0) return fmtNaira(0, emptyCurrency);
  return items.map((i) => fmtNaira(i.amount, i.currency)).join("  +  ");
}

export function fmtNaira(n, currency = "NGN") {
  const num = Number(n || 0);
  const str = num.toLocaleString("en-NG", {
    maximumFractionDigits: Number.isInteger(num) ? 0 : 2,
    minimumFractionDigits: 0,
  });
  return `${symbolFor(currency)}${str}`;
}

export function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" });
}

export function fmtDateShort(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-NG", { day: "numeric", month: "short" });
}

// Human label for a debt's timing
export function dueLabel(debt) {
  if (!debt) return "";
  if (debt.status === "paid") return "Paid in full";
  if (debt.display_status === "overdue") {
    const d = debt.days_overdue;
    return `${d} day${d === 1 ? "" : "s"} overdue`;
  }
  if (debt.display_status === "due_soon") {
    const d = debt.days_until_due;
    if (d === 0) return "Due today";
    if (d === 1) return "Due tomorrow";
    return `Due in ${d} days`;
  }
  if (debt.due_date) return `Due ${fmtDate(debt.due_date)}`;
  return "No due date set";
}

// Normalise a phone number to international format for wa.me (Nigeria default)
export function normalizePhone(phone) {
  if (!phone) return "";
  let p = String(phone).replace(/[^\d]/g, "");
  if (p.startsWith("00")) p = p.slice(2);
  if (p.startsWith("0")) p = "234" + p.slice(1);
  else if (!p.startsWith("234") && p.length <= 10) p = "234" + p;
  return p;
}

export function whatsappLink(phone, message) {
  const p = normalizePhone(phone);
  const text = encodeURIComponent(message);
  // Use api.whatsapp.com/send directly: the wa.me redirect corrupts 4-byte
  // UTF-8 emoji (👋 🙏) into replacement characters.
  return p
    ? `https://api.whatsapp.com/send?phone=${p}&text=${text}`
    : `https://api.whatsapp.com/send?text=${text}`;
}

// Canonical public base for all shareable links (acknowledgement / repayment /
// invitations). Prefers the configured public domain, falls back to the origin
// the app is currently served from.
export function publicUrl(path) {
  const base = (process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin).replace(/\/$/, "");
  return `${base}${path}`;
}

// Two-sided helpers. direction: "owed_to_me" | "i_owe"
export function isIOwe(debt) {
  return (debt?.direction || "owed_to_me") === "i_owe";
}

// Settlement badge meta for a fully-repaid debt (settlement_status field)
export function settlementMeta(status) {
  switch (status) {
    case "settled_confirmed":
      return { label: "Settled", tone: "emerald" };
    case "awaiting_confirmation":
      return { label: "Fully repaid — awaiting confirmation", tone: "amber" };
    case "questioned":
      return { label: "Repayment questioned", tone: "amber" };
    case "settled_recorded":
      return { label: "Settled — recorded by you", tone: "slate" };
    default:
      return { label: "Paid", tone: "emerald" };
  }
}

// Per-payment confirmation badge meta
export function confirmationMeta(status, personName) {
  switch (status) {
    case "pending":
      return { label: "Awaiting confirmation", tone: "amber" };
    case "confirmed":
      return { label: personName ? `Confirmed by ${personName}` : "Confirmed", tone: "emerald" };
    case "questioned":
      return { label: "Questioned", tone: "amber" };
    default:
      return { label: "Recorded", tone: "slate" };
  }
}
